import Action from 'actions';
import BaseExecutor from 'services/sports/default/base-executor';
import {groupObjectsByProp} from 'helpers/array-helper';
import Const from 'constants';
import {isPlayerOnDoublePosition, preparePlayerUID} from 'helpers/player-helper';
import {calcQuantityOfReservedLineupsByExposure, countPlayerIdInLineups} from 'helpers/lineup-helper';
import {calcAndRoundDownValByPct, calcPercentage} from 'helpers/common-helper';
import {sortByProperty} from 'helpers/sorters';
import {
    addRoleSuffix,
    extractEntityIdFromSimplexItem,
    getComparisonTypeByNumber,
    isUnitPrefixInSimplexItem,
    getSimplexUnitName,
} from 'helpers/simplex-helper';
import {getSimplexService, getPlayerService} from 'services/sports/factory';
import NumberHelper, {toFloat} from 'helpers/number-helper';

export default class BaseLp extends BaseExecutor {
    constructor(incomingValues = {}) {
        super();
        const {store} = incomingValues;
        if (store) {
            this.store = store;
        }
    }

    preparePlayerByLPPlayerId({incomingValues: {id}}) {
        const {
            playersData: {players},
        } = Action.getState();

        const [playerId, positionKey] = id.replace(Const.LP_UNIT_PREFIX, '').split(Const.LP_ITEM_DELIMITER);

        const player = players.getPlayer(playerId);

        if (player === null) {
            throw new Error('Player Not Found.');
        }

        const position = positionKey ? Number(positionKey) : Number(player.position[0]);

        return {...player, position};
    }

    preparePlayerByLPPlayerId_ShowDown({incomingValues: {id}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let unit = id.replace(Const.LP_UNIT_PREFIX, '').split(Const.LP_ITEM_DELIMITER);

        let player = {...playersGroupedById[unit[0]]};

        if (Const.LP_CAPTAIN_PLAYER_POSITION == unit[1]) {
            player.setAsCaptain();
        }

        return player;
    }

    prepareSectionByMultiPositions({incomingValues = {}}) {
        const state = Action.getState();
        const {playersLock} = state;
        const {preparedPlayers} = state.playersData;
        const players = preparedPlayers.groupedBy.doublePosition();

        let result = '';

        if (!isEmpty(players)) {
            players.map((player) => {
                let str = '';
                player.position.map((pos) => {
                    str += '+' + getSimplexUnitName([player.playerId, pos]);
                });
                if (playersLock.isLockedAnywhere(player.getId())) {
                    result += str + '=1\n';
                } else {
                    result += str + '<=1\n';
                }
            });
        }
        // console.log('multiPositions',result);
        return result;
    }

    prepareSectionBySalary({incomingValues = {}}) {
        const {sportConfig} = Action.getState();
        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'salary',
            {
                checkIfPlayerOnDoublePosition: true,
                ...incomingValues,
            },
            [],
        );

        let rules = '';

        if (!isEmpty(constraints)) {
            rules = this.exec(
                'convertConstraintsToStr',
                {
                    constraints,
                },
                '',
            );
        }

        return rules;
    }

    prepareSectionBySalary_ShowDown({incomingValues = {}}) {
        const {salaryCap, playersData} = Action.getState();
        const {preparedPlayers} = playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let statement = '';
        Object.keys(playersGroupedById).map((pId) => {
            const player = playersGroupedById[pId];
            statement += addRoleSuffix({
                prefix: '+' + player.captain_salary + getSimplexUnitName(pId),
                role: Const.CAPTAIN_MODE,
            });
            statement += addRoleSuffix({
                prefix: '+' + player.salary + getSimplexUnitName(pId),
            });
        });
        let result = statement + Const.STACKS_TYPE_AT_MOST + salaryCap.getInnerMax();

        if (!salaryCap.isInnerMinDefault()) {
            result += '\n' + statement + Const.STACKS_TYPE_AT_LEAST + salaryCap.getInnerMin();
        }

        return result;
    }

    prepareSectionBySalaryRoles({incomingValues: {salaryPropsByRoles}}) {
        const state = Action.getState();
        const {salaryCap} = state;
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let statement = '';
        Object.keys(playersGroupedById).map((pId) => {
            let player = playersGroupedById[pId];

            Object.keys(salaryPropsByRoles).map((role) => {
                const salaryPropName = salaryPropsByRoles[role];
                let prefix = '+' + player[salaryPropName] + getSimplexUnitName(pId);

                statement += addRoleSuffix({prefix, role});
            });
        });
        let result = statement + Const.STACKS_TYPE_AT_MOST + salaryCap.getInnerMax();
        if (!salaryCap.isInnerMinDefault()) {
            result += '\n' + statement + Const.STACKS_TYPE_AT_LEAST + salaryCap.getInnerMin();
        }
        // console.log("salary\n",result);
        return result;
    }

    prepareSectionByPlayer({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();

        let result = '',
            addedPlayers = [];
        players.map((player) => {
            if (!inArray(player.uniqueId, addedPlayers)) {
                result += '+' + getSimplexUnitName(player.uniqueId);
                addedPlayers.push(player.uniqueId);
            }
        });
        result += '=' + sportConfig.getPlayersInTeam();
        // console.log('player',result);
        return result;
    }

    prepareSectionByPlayer_ShowDown({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();

        let result = '';
        Object.keys(players).map((playerId) => {
            let prefix = '+' + getSimplexUnitName(playerId);
            result += addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
            result += addRoleSuffix({prefix});
        });
        result += '=' + sportConfig.getPlayersInTeam();
        // console.log("player\n",result);
        return result;
    }

    prepareSectionByPosition({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();

        let rules = '';

        const positions = sportConfig.getConfigProp('positions');
        Object.keys(positions).map((positionNumber) => {
            const simplexUnitNamesByTeams = {};
            const addedPlayers = [];
            const posObj = positions[positionNumber];

            players.map((player) => {
                if (player.position != positionNumber || inArray(player.uniqueId, addedPlayers)) {
                    return;
                }

                if (!simplexUnitNamesByTeams.hasOwnProperty(player.team)) {
                    simplexUnitNamesByTeams[player.team] = '';
                }

                simplexUnitNamesByTeams[player.team] += '+' + getSimplexUnitName(player.uniqueId);

                addedPlayers.push(player.uniqueId);
            });

            if (isEmpty(simplexUnitNamesByTeams)) {
                return;
            }

            let positionPlayers = '';

            Object.values(simplexUnitNamesByTeams).map((simplexUnitNames) => {
                if (posObj.maxFromOneTeam) {
                    rules += simplexUnitNames + '<=' + posObj.maxFromOneTeam + '\n';
                } else {
                    positionPlayers += simplexUnitNames;
                }
            });

            if (positionPlayers) {
                const {maxPlayers, minPlayers} = posObj;

                if (maxPlayers == minPlayers) {
                    rules += positionPlayers + '=' + maxPlayers + '\n';
                } else {
                    if (Boolean(minPlayers)) {
                        rules += positionPlayers + '=>' + minPlayers + '\n';
                    }
                    if (Boolean(maxPlayers)) {
                        rules += positionPlayers + '<=' + maxPlayers + '\n';
                    }
                }
            }
        });
        // console.log("prepareSectionByPosition\n",rules);
        return rules;
    }

    prepareSectionByTeam({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
            lineupsData: {filters: lineupsFilter},
        } = Action.getState();
        const playersGroupedByAllowedPositions = preparedPlayers.groupedBy.allowedPositions();
        const teams = groupObjectsByProp(playersGroupedByAllowedPositions, 'team');

        let result = '';

        Object.keys(teams).map((teamName) => {
            let players = teams[teamName],
                rivalTeamStr = '',
                homeTeamStr = '',
                addedPlayers = [],
                defencePlayerPositions = sportConfig.getConfigProp('defencePlayerPositions'),
                maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers');
            // If value of opposingPlayers isn't null,
            // try to find a player from a rival team.
            if (null !== lineupsFilter.opposingPlayers && players[0].opponent in teams) {
                // If the rival team was found,
                // iterate players from one and define a needed player from
                // defencePlayerPositions.
                let oppPlayers = teams[players[0].opponent];
                for (let k in oppPlayers) {
                    //If the player from rival team was found, add him to teamStr.
                    if (inArray(oppPlayers[k].position, defencePlayerPositions)) {
                        let residual = maxSingleTeamPlayers - lineupsFilter.opposingPlayers;
                        rivalTeamStr += '+' + residual + getSimplexUnitName(oppPlayers[k].uniqueId);
                    }
                }
                players.map((player) => {
                    if (!inArray(player.uniqueId, addedPlayers) && !inArray(player.position, defencePlayerPositions)) {
                        homeTeamStr += '+' + getSimplexUnitName(player.uniqueId);
                        addedPlayers.push(player.uniqueId);
                    }
                });
            } else {
                players.map((player) => {
                    if (!inArray(player.uniqueId, addedPlayers)) {
                        homeTeamStr += '+' + getSimplexUnitName(player.uniqueId);
                        addedPlayers.push(player.uniqueId);
                    }
                });
            }

            if (homeTeamStr) {
                result += rivalTeamStr + homeTeamStr + '<=' + maxSingleTeamPlayers + '\n';
            }
        });

        result += this.exec('limitPositions', {teams}, '');
        result += this.exec('minTeams', {teams}, '');

        return result;
    }

    prepareSectionByTeam_ShowDown({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const teams = preparedPlayers.groupedBy.property('team', []);

        let result = '';
        Object.keys(teams).map((teamName) => {
            let players = teams[teamName];
            players.map((player) => {
                let prefix = '+' + getSimplexUnitName(player.playerId);
                result += addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
                result += addRoleSuffix({prefix});
            });
            result += '<=' + sportConfig.getConfigProp('maxSingleTeamPlayers') + '\n';
        });

        return result;
    }

    prepareSectionByTier({incomingValues = {}}) {
        const state = Action.getState();
        const {sportConfig} = state;
        const {preparedPlayers, slates} = state.playersData;
        const playersGroupedByPosition = preparedPlayers.groupedBy.position();
        const slate = slates.getById(sportConfig.getSlateId());

        function assertTiersAreFilled(filledTiersQuantity) {
            if (filledTiersQuantity < slate.tiers_quantity) {
                throw new Error('Not enough players to generate lineup.');
            }
        }

        function shouldTierBeSkipped(tierNumber) {
            return tierNumber > slate.tiers_quantity;
        }

        let result = '';
        if (!isEmpty(playersGroupedByPosition)) {
            let players = {};
            playersGroupedByPosition.map((player) => {
                const tier = player.rosterPositionName;
                if (!players.hasOwnProperty(tier)) {
                    players[tier] = [];
                }
                players[tier].push(player);
            });

            assertTiersAreFilled(Object.keys(players).length);

            Object.keys(players).map((tier) => {
                players[tier].map((player) => {
                    result += '+' + getSimplexUnitName(player.uniqueId);
                });
                if (shouldTierBeSkipped(tier)) {
                    result += '=0\n';
                } else {
                    result += '=1\n';
                }
            });
        }
        // console.log("tiers\n",result);
        return result;
    }

    prepareSectionByUniquePosition_ShowDown({incomingValues = {}}) {
        const state = Action.getState();
        const {playersLock} = state;
        const {preparedPlayers} = state.playersData;
        const players = preparedPlayers.groupedBy.playerId();
        let result = '';

        Object.values(players).map((player) => {
            let prefix = '+' + getSimplexUnitName(player.getId());

            const locked = [],
                notLocked = [];
            if (playersLock.isLocked(player.getId(), Const.CAPTAIN_MODE)) {
                locked.push(addRoleSuffix({prefix, role: Const.CAPTAIN_MODE}));
            } else {
                notLocked.push(addRoleSuffix({prefix, role: Const.CAPTAIN_MODE}));
            }

            if (playersLock.isLocked(player.getId(), Const.FLEX_MODE)) {
                locked.push(addRoleSuffix({prefix, role: Const.FLEX_MODE}));
            } else {
                notLocked.push(addRoleSuffix({prefix, role: Const.FLEX_MODE}));
            }

            if (!isEmpty(locked)) {
                result += locked.join('') + '=1\n';
                if (!isEmpty(notLocked)) {
                    result += notLocked.join('') + '=0\n';
                }
            } else if (!isEmpty(notLocked)) {
                result += notLocked.join('') + '<=1\n';
            }
        });
        // console.log("uniquePositions\n",result);
        return result;
    }

    projectedOwnership({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'projectedOwnership',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    playerCombinationStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'playerCombinationStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    forceOfProjectedOwnership({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'forceOfProjectedOwnership',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    keyPlayerStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'keyPlayerStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    keyPlayerStackingComboExpMax({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'keyPlayerStackingComboExpMax',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    teamNameStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'teamNameStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    prepareSectionByStacks_ShowDown({incomingValues: {modes = [Const.DEFAULT_MODE, Const.CAPTAIN_MODE]}}) {
        const {
            stacks,
            playersData: {preparedPlayers},
        } = Action.getState();
        const {groups: groupsOfStacks} = stacks;
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        // console.log('groupsOfStacks',groupsOfStacks);
        let result = '';
        if (!groupsOfStacks.hasItems()) {
            return result;
        }

        const createStr = function (playerId, factor = 1) {
            let str = '',
                prefix = '';

            if (playersIndexedById.hasOwnProperty(playerId)) {
                if (1 === Number(factor)) {
                    prefix += '+' + getSimplexUnitName(playerId);
                } else if (factor > 1) {
                    prefix += '+' + factor + getSimplexUnitName(playerId);
                } else {
                    prefix += factor + getSimplexUnitName(playerId);
                }

                modes.map((mode) => (str += addRoleSuffix({prefix, role: mode})));
            }

            return str;
        };

        const createAtLeastCondition = function (players, factor) {
            let condition = '';
            players.map((playerId) => {
                condition += createStr(playerId, factor);
            });
            if (condition.length) {
                condition += Const.STACKS_TYPE_AT_LEAST + '0\n';
            }
            return condition;
        };

        const createAtMostCondition = function (players, factor, quantityOfPlayers) {
            let condition = '';
            players.map((playerId) => {
                condition += createStr(playerId, factor);
            });
            if (condition.length) {
                condition += Const.STACKS_TYPE_AT_MOST + quantityOfPlayers + '\n';
            }
            return condition;
        };

        groupsOfStacks.all().map((group) => {
            const players = group.players.map((playerId) => createStr(playerId)).join('');

            if (group.hasOwnProperty('keys')) {
                if (Const.STACKS_TYPE_EXACTLY === group.condition) {
                    let atLeastCond = createAtLeastCondition(group.keys, -group.limit);
                    if (atLeastCond) {
                        result += players + atLeastCond;
                    }

                    let atMost = createAtMostCondition(
                        group.keys,
                        group.players.length - group.limit,
                        group.players.length,
                    );
                    if (atMost && group.limit < group.players.length) {
                        result += players + atMost;
                    }
                } else if (Const.STACKS_TYPE_AT_LEAST === group.condition) {
                    let atLeastCond = createAtLeastCondition(group.keys, -group.limit);
                    if (atLeastCond) {
                        result += players + atLeastCond;
                    }
                } else if (Const.STACKS_TYPE_AT_MOST === group.condition) {
                    let atMost = createAtMostCondition(
                        group.keys,
                        group.players.length - group.limit,
                        group.players.length,
                    );
                    if (atMost) {
                        result += players + atMost;
                    }
                }
            } else {
                if (Const.STACKS_TYPE_EXACTLY === group.condition) {
                    result += players + Const.STACKS_TYPE_AT_LEAST + group.limit + '\n';
                    result += players + Const.STACKS_TYPE_AT_MOST + group.limit + '\n';
                } else {
                    result += players + group.condition + group.limit + '\n';
                }
            }
        });
        // console.log("stacks\n", result);
        return result;
    }

    prepareSectionByCptStacks({incomingValues = {}}) {
        const {
            cptPositionStacking,
            playersData: {preparedPlayers},
        } = Action.getState();
        const groupsOfStacks = cptPositionStacking.getItems();
        const players = preparedPlayers.groupedBy.playerId();

        let result = '';
        if (!cptPositionStacking.isEnabled() || isEmpty(groupsOfStacks)) {
            return result;
        }

        const createStr = function (playerId, factor = 1) {
            let str = '';

            if (players.hasOwnProperty(playerId)) {
                if (1 === Number(factor)) {
                    str += '+' + getSimplexUnitName(playerId);
                } else if (factor > 1) {
                    str += '+' + factor + getSimplexUnitName(playerId);
                } else {
                    str += factor + getSimplexUnitName(playerId);
                }
            }

            return str;
        };

        const createAtLeastCondition = function (playerId, isCpt, factor) {
            const prefix = createStr(playerId, factor);
            let condition = '';
            if (prefix) {
                const args = {prefix};
                if (isCpt) {
                    args.role = Const.CAPTAIN_MODE;
                }
                condition += addRoleSuffix(args);
                condition += Const.STACKS_TYPE_AT_LEAST + '0\n';
            }
            return condition;
        };

        const createAtMostCondition = function (playerId, isCpt, factor, quantityOfPlayers) {
            const prefix = createStr(playerId, factor);
            let condition = '';
            if (prefix) {
                const args = {prefix};
                if (isCpt) {
                    args.role = Const.CAPTAIN_MODE;
                }
                condition += addRoleSuffix(args);
                condition += Const.STACKS_TYPE_AT_MOST + quantityOfPlayers + '\n';
            }
            return condition;
        };

        Object.values(groupsOfStacks).map((group) => {
            /**
             * if stack type is "at least" and number of players is 0,
             * move to next the iteration.
             * OR
             * if stack type is "at most" and number of players is less than limit or equal its,
             * move to next the iteration.
             */
            if (group.isTypeAtLeast() && group.getLimit() === 0) {
                return;
            } else if (group.isTypeAtMost() && group.getStackedPlayersQuantity() <= group.getLimit()) {
                return;
            }

            let players = '';
            group.getStackedPlayerIds().map((playerId) => {
                const prefix = createStr(playerId);
                if (prefix) {
                    if (!group.isKeyPlayerCpt()) {
                        players += addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
                    }
                    players += addRoleSuffix({prefix});
                }
            });

            if (group.isTypeExactly()) {
                let atLeastCond = createAtLeastCondition(
                    group.getKeyPlayerId(),
                    group.isKeyPlayerCpt(),
                    -1 * group.getLimit(),
                );

                if (atLeastCond) {
                    result += players + atLeastCond;
                }

                let atMost = createAtMostCondition(
                    group.getKeyPlayerId(),
                    group.isKeyPlayerCpt(),
                    group.getStackedPlayersQuantity() - group.getLimit(),
                    group.getStackedPlayersQuantity(),
                );
                if (atMost && group.getLimit() < group.getStackedPlayersQuantity()) {
                    result += players + atMost;
                }
            } else if (group.isTypeAtLeast()) {
                let atLeastCond = createAtLeastCondition(
                    group.getKeyPlayerId(),
                    group.isKeyPlayerCpt(),
                    -1 * group.getLimit(),
                );

                if (atLeastCond) {
                    result += players + atLeastCond;
                }
            } else if (group.isTypeAtMost()) {
                let atMost = createAtMostCondition(
                    group.getKeyPlayerId(),
                    group.isKeyPlayerCpt(),
                    group.getStackedPlayersQuantity() - group.getLimit(),
                    group.getStackedPlayersQuantity(),
                );
                if (atMost) {
                    result += players + atMost;
                }
            }
        });
        // console.log("CptStacks\n", result);
        return result;
    }

    prepareSectionByDominatorStack({incomingValues = {}}) {
        const state = Action.getState();
        const {
            groups: {data: groupsOfStacks},
        } = state.stacks;
        const {preparedPlayers} = state.playersData;
        const playersGroupedByPlayerId = preparedPlayers.groupedBy.playerId();

        let result = '';
        if (!groupsOfStacks.length) {
            return result;
        }

        const createStr = function (playerId, factor) {
            factor = factor || 1;
            let str = '';

            if (
                playersGroupedByPlayerId.hasOwnProperty(playerId) &&
                isPlayerOnDoublePosition(playersGroupedByPlayerId[playerId])
            ) {
                playersGroupedByPlayerId[playerId].position.map((pos) => {
                    str += createStr(playerId + Const.LP_ITEM_DELIMITER + pos, factor);
                });
            } else {
                if (1 == factor) {
                    str += '+' + getSimplexUnitName(playerId);
                } else if (factor > 1) {
                    str += '+' + factor + getSimplexUnitName(playerId);
                } else {
                    str += factor + getSimplexUnitName(playerId);
                }
            }

            return str;
        };

        const createAtLeastCondition = function (players, factor) {
            let condition = '';
            players.map((playerId) => {
                condition += createStr(playerId, factor);
            });

            if (condition.length) {
                condition += Const.STACKS_TYPE_AT_LEAST + '0\n';
            }

            return condition;
        };

        const createAtMostCondition = function (players, factor, quantityOfPlayers) {
            let condition = '';
            players.map((playerId) => {
                condition += createStr(playerId, factor);
            });

            if (condition.length) {
                condition += Const.STACKS_TYPE_AT_MOST + quantityOfPlayers + '\n';
            }

            return condition;
        };

        if (1 < groupsOfStacks.length) {
            groupsOfStacks.map((group) => {
                let players = '';
                group.players.map((playerId) => {
                    players += createStr(playerId);
                });

                if (group.hasOwnProperty('keys')) {
                    if (Const.STACKS_TYPE_EXACTLY === group.condition) {
                        const atLeastCond = createAtLeastCondition(group.keys, -group.limit);
                        if (atLeastCond) {
                            result += players + atLeastCond;
                        }

                        const atMost = createAtMostCondition(
                            group.keys,
                            group.players.length - group.limit,
                            group.players.length,
                        );
                        if (atMost && group.limit < group.players.length) {
                            result += players + atMost;
                        }
                    } else if (Const.STACKS_TYPE_AT_LEAST === group.condition) {
                        const atLeastCond = createAtLeastCondition(group.keys, -group.limit);
                        if (atLeastCond) {
                            result += players + atLeastCond;
                        }
                    } else if (Const.STACKS_TYPE_AT_MOST === group.condition) {
                        const atMost = createAtMostCondition(
                            group.keys,
                            group.players.length - group.limit,
                            group.players.length,
                        );
                        if (atMost) {
                            result += players + atMost;
                        }
                    }
                } else {
                    if (Const.STACKS_TYPE_EXACTLY === group.condition) {
                        result += players + Const.STACKS_TYPE_AT_LEAST + group.limit + '\n';
                        result += players + Const.STACKS_TYPE_AT_MOST + group.limit + '\n';
                    } else {
                        result += players + group.condition + group.limit + '\n';
                    }
                }
            });
        } else {
            const group = groupsOfStacks[0];

            let players = '';
            group.players.map((playerId) => {
                players += createStr(playerId);
            });

            if (Const.STACKS_TYPE_EXACTLY === group.condition) {
                result += players + Const.STACKS_TYPE_AT_LEAST + group.limit + '\n';
                result += players + Const.STACKS_TYPE_AT_MOST + group.limit + '\n';
            } else {
                result += players + group.condition + group.limit + '\n';
            }
        }
        // console.log("stacks\n", result);
        return result;
    }

    prepareSectionByPassReceiver({incomingValues = {}}) {
        const {
            lineupsData: {
                filters: {avoidFewPassReceivers},
            },
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();

        let result = '';

        if (settings.isRelaxBuildRulesUsed() || !avoidFewPassReceivers) {
            return result;
        }

        const teams = preparedPlayers.groupedBy.teamAndPosition();

        Object.keys(teams).map((key) => {
            let team = teams[key].positions;
            let passReceivers = [];
            let row = '';

            Const.NFL_PASS_RECEIVER_POSITIONS.map((pos) => {
                if (team.hasOwnProperty(pos)) {
                    team[pos].map((player) => {
                        passReceivers.push(player.playerId);
                    });
                }
            });

            if (passReceivers.length) {
                row = '+' + passReceivers.map((playerId) => getSimplexUnitName(playerId)).join('+');
                if (team.hasOwnProperty('QB')) {
                    team['QB'].map((player) => {
                        row += '-' + (passReceivers.length - 1) + getSimplexUnitName(player.playerId);
                    });
                }
                result += row + Const.STACKS_TYPE_AT_MOST + '1\n';
            }
        });
        // console.log("passReceiver\n", result);
        return result;
    }

    prepareSectionByPositionWithFlexMaxExposure({incomingValues: {flexPositionsQuantity = 1}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const positions = sportConfig.getConfigProp('positions');
        const players = preparedPlayers.groupedBy.allowedPositions();
        const forbiddenFlexPositions = this.exec('getForbiddenFlexMaxExposurePositions', {flexPositionsQuantity});

        let result = '';
        Object.keys(positions).map((pos) => {
            let playersByTeam = {};
            let addedPlayers = [];
            let posObj = positions[pos];
            let isPositionFlex = posObj.id === 'flex';

            players.map((player) => {
                if (player.position != pos || inArray(player.uniqueId, addedPlayers)) {
                    return;
                }

                if (!playersByTeam.hasOwnProperty(player.team)) {
                    playersByTeam[player.team] = '';
                }

                playersByTeam[player.team] += '+' + getSimplexUnitName(player.uniqueId);

                addedPlayers.push(player.uniqueId);
            });

            let str = '';
            Object.values(playersByTeam).map((teamPlayers) => {
                if (posObj.maxFromOneTeam) {
                    result += teamPlayers + '<=' + posObj.maxFromOneTeam + '\n';
                } else {
                    str += teamPlayers;
                }
            });

            if (!str) {
                return;
            }

            let {minPlayers, maxPlayers, shortName} = posObj;

            if (!isPositionFlex && !isEmpty(forbiddenFlexPositions)) {
                if (inArray(shortName, Object.keys(forbiddenFlexPositions))) {
                    maxPlayers -= forbiddenFlexPositions[shortName];
                }
            }

            if (maxPlayers === minPlayers) {
                result += str + '=' + maxPlayers + '\n';
            } else {
                if (minPlayers) {
                    result += str + '=>' + minPlayers + '\n';
                }
                if (maxPlayers) {
                    result += str + '<=' + maxPlayers + '\n';
                }
            }
        });

        return result;
    }

    prepareSectionByGame({incomingValues: {addPositionToPID = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers, slates},
        } = Action.getState();
        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');
        const playersInLineupQuantity = sportConfig.isGameTypeTiers()
            ? slates.getById(sportConfig.getSlateId()).tiers_quantity
            : sportConfig.getConfigProp('playersInTeam');

        const maxSingleGamePlayers = playersInLineupQuantity - 1;

        const result = [];
        Object.values(playersByGames).map((players) => {
            const simplexUnits = [];
            players.map((player) => {
                if (addPositionToPID) {
                    const playerUIDs = preparePlayerUID(player);
                    playerUIDs.map((playerUID) => simplexUnits.push('+' + getSimplexUnitName(playerUID)));
                } else {
                    simplexUnits.push('+' + getSimplexUnitName(player.playerId));
                }
            });

            if (simplexUnits.length > maxSingleGamePlayers) {
                result.push(simplexUnits.join('') + '<=' + maxSingleGamePlayers);
            }
        });

        return result.join('\n');
    }

    maxSingleGamePlayersByModes({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'maxSingleGamePlayersByModes',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    prepareSectionByMinGames2AndMinTeams2({incomingValues: {addPositionToPID = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');

        const minLimit = 2;

        let result = '';

        if (minLimit == sportConfig.minGames && minLimit == sportConfig.minTeams) {
            const positionLimit = sportConfig.getConfigProp('playersInTeam') - 1;
            Object.values(playersByGames).map((players) => {
                let arr = [];
                players.map((player) => {
                    if (addPositionToPID) {
                        let playerUIDs = preparePlayerUID(player);
                        playerUIDs.map((playerUID) => arr.push('+' + getSimplexUnitName(playerUID)));
                    } else {
                        arr.push('+' + getSimplexUnitName(player.playerId));
                    }
                });
                if (arr.length > positionLimit) {
                    result += arr.join('') + '<=' + positionLimit + '\n';
                }
            });
        }

        return result;
    }

    prepareSectionByHittersStack({incomingValues = {}}) {
        const {
            stacks: {hitters},
            lineupsQuantity,
            lineupGenerator: {settings},
            playersData: {preparedPlayers},
            lineupsData: {filters: lineupsFilter},
        } = Action.getState();

        let result = '';
        if (settings.isRelaxBuildRulesUsed() || !hitters.isEnabled()) {
            return result;
        }

        let hittersStack = this.store.get('hittersStack', {});
        const playersGroupedByTeam = preparedPlayers.groupedBy.property('team');

        const createStackConstraintsObj = function (groupNumber, fromLineupsQuantity, parentTeamName = null) {
            let obj = {
                    teams: [],
                    lastUsedTeam: null,
                },
                stack = {...hitters.getGroup(groupNumber)},
                items = [...stack.items];

            items.sort(sortByProperty('expMin'));
            items.map((item) => {
                if (parentTeamName === item.team) {
                    return;
                }

                if (!playersGroupedByTeam.hasOwnProperty(item.team)) {
                    return;
                }

                const players = playersGroupedByTeam[item.team];
                // console.log('playersGroupedByPlayerId',cp(playersGroupedByPlayerId))
                const row = [];
                if (isEmpty(item.players)) {
                    players.map((player) => {
                        if (!player.isPitcher() && !player.isExcludedAnywhere()) {
                            row.push(
                                preparePlayerUID(player)
                                    .map((playerId) => getSimplexUnitName(playerId))
                                    .join('+'),
                            );
                        }
                    });
                } else {
                    item.players.map((player) => {
                        if (!player.isExcludedAnywhere()) {
                            row.push(
                                preparePlayerUID(player)
                                    .map((playerId) => getSimplexUnitName(playerId))
                                    .join('+'),
                            );
                        }
                    });
                }

                if (row.length < stack.playersQuantity) {
                    return;
                }

                const playersStr = '+' + row.join('+') + '=' + stack.playersQuantity + '\n';

                obj.teams.push({
                    name: item.team,
                    playersStr,
                    expMin: item.expMin,
                    reservedLineups: calcAndRoundDownValByPct(fromLineupsQuantity, item.expMin),
                });
            });

            return {...obj};
        };

        const createConstraintsStr = function (comingStack) {
            let iterator = makeIterator(comingStack.teams);
            if (null !== comingStack.lastUsedTeam) {
                iterator.setKey(comingStack.lastUsedTeam);
                iterator.next();
            }

            let str = '';
            for (let i = 0; i < comingStack.teams.length; i++) {
                if (!iterator.valid()) {
                    iterator.rewind();
                }

                let team = iterator.current();

                if (0 < team.reservedLineups) {
                    // console.log('team',team);
                    str += team.playersStr;
                    team.reservedLineups--;
                    comingStack.lastUsedTeam = iterator.key();
                    if (!isEmpty(team.stack)) {
                        str += createConstraintsStr(team.stack);
                    }
                    break;
                }

                iterator.next();
            }
            return str;
        };

        const distributeFreeLineups = function (comingStack, lineupsQuantity) {
            if (isEmpty(comingStack.teams)) {
                return comingStack;
            }

            let freeLineups = lineupsQuantity;
            comingStack.teams.map((team) => {
                freeLineups -= team.reservedLineups;
            });
            // console.log('lineupsQuantity',lineupsQuantity);
            // console.log('freeLineups',freeLineups);
            if (freeLineups) {
                let iterator = makeIterator(comingStack.teams),
                    filledTeamsQuantity = 0;
                for (let i = 0; i < freeLineups; i++) {
                    if (!iterator.valid()) {
                        iterator.rewind();
                    }

                    let team = iterator.current(),
                        reservedLineupsPercent = calcPercentage(lineupsQuantity, team.reservedLineups);

                    if (team.expMin > reservedLineupsPercent) {
                        team.reservedLineups++;
                    } else {
                        filledTeamsQuantity++;
                    }

                    if (filledTeamsQuantity >= comingStack.teams.length) {
                        break;
                    }

                    iterator.next();
                }
            }

            return comingStack;
        };

        if (isEmpty(hittersStack)) {
            hittersStack = createStackConstraintsObj(Const.FIRST_HITTER_GROUP, lineupsQuantity.getLineupsQuantity());

            hittersStack = distributeFreeLineups({...hittersStack}, lineupsQuantity.getLineupsQuantity());
            if (hitters.isDoubleStackModeEnabled()) {
                hittersStack.teams.map((team) => {
                    let secondStack = createStackConstraintsObj(
                        Const.SECOND_HITTER_GROUP,
                        team.reservedLineups,
                        team.name,
                    );
                    if (!isEmpty(secondStack.teams)) {
                        team.stack = distributeFreeLineups({...secondStack}, team.reservedLineups);
                    }
                });
            }
        }

        result += createConstraintsStr(hittersStack);

        this.store.set('hittersStack', hittersStack);
        // console.log("result\n",result);
        return result;
    }

    prepareSectionByNHLNameStack({incomingValues = {}}) {
        const state = Action.getState();
        const {nhlName} = state.stacks;

        let result = '';
        if (!nhlName.useStack) {
            return result;
        }

        let nhlNameStack = this.store.get('nhlNameStack', {});
        const {preparedPlayers} = state.playersData;
        const {lineupsQuantity} = state;
        const playersGroupedByTeam = preparedPlayers.groupedBy.property('team');

        const createStackConstraintsObj = function (groupNumber, fromLineupsQuantity, parentTeamName = null) {
            let obj = {
                    teams: [],
                    lastUsedTeam: null,
                },
                stack = {...nhlName.groups[groupNumber]},
                items = [...stack.items];

            items.sort(sortByProperty('expMin'));
            items.map((item) => {
                if (parentTeamName === item.team) {
                    return;
                }
                if (!playersGroupedByTeam.hasOwnProperty(item.team)) {
                    return;
                }
                let str = '';
                if (isEmpty(item.players)) {
                    let players = playersGroupedByTeam[item.team];
                    players.map((player) => {
                        if (!player.isGoalie()) {
                            str +=
                                '+' +
                                preparePlayerUID(player)
                                    .map((playerId) => getSimplexUnitName(playerId))
                                    .join('+');
                        }
                    });
                } else {
                    item.players.map((player) => {
                        str +=
                            '+' +
                            preparePlayerUID(player)
                                .map((playerId) => getSimplexUnitName(playerId))
                                .join('+');
                    });
                }
                if (!str) {
                    return;
                }
                str += '=' + stack.playersQuantity + '\n';
                obj.teams.push({
                    name: item.team,
                    playersStr: str,
                    expMin: item.expMin,
                    reservedLineups: calcAndRoundDownValByPct(fromLineupsQuantity, item.expMin),
                });
            });

            return {...obj};
        };

        const createConstraintsStr = function (comingStack) {
            let iterator = makeIterator(comingStack.teams);
            if (null !== comingStack.lastUsedTeam) {
                iterator.setKey(comingStack.lastUsedTeam);
                iterator.next();
            }

            let str = '';
            for (let i = 0; i < comingStack.teams.length; i++) {
                if (!iterator.valid()) {
                    iterator.rewind();
                }

                let team = iterator.current();

                if (0 < team.reservedLineups) {
                    str += team.playersStr;
                    team.reservedLineups--;
                    comingStack.lastUsedTeam = iterator.key();
                    if (!isEmpty(team.stack)) {
                        str += createConstraintsStr(team.stack);
                    }
                    break;
                }

                iterator.next();
            }
            return str;
        };

        const distributeFreeLineups = function (comingStack, lineupsQuantity) {
            let freeLineups = lineupsQuantity;
            comingStack.teams.map((team) => {
                freeLineups -= team.reservedLineups;
            });
            if (freeLineups) {
                let iterator = makeIterator(comingStack.teams),
                    filledTeamsQuantity = 0;
                for (let i = 0; i < freeLineups; i++) {
                    if (!iterator.valid()) {
                        iterator.rewind();
                    }

                    let team = iterator.current(),
                        reservedLineupsPercent = calcPercentage(lineupsQuantity, team.reservedLineups);

                    if (team.expMin > reservedLineupsPercent) {
                        team.reservedLineups++;
                    } else {
                        filledTeamsQuantity++;
                    }

                    if (filledTeamsQuantity >= comingStack.teams.length) {
                        break;
                    }

                    iterator.next();
                }
            }

            return comingStack;
        };

        if (isEmpty(nhlNameStack)) {
            nhlNameStack = createStackConstraintsObj(
                Const.NHL_NAME_STACK_FIRST_GROUP,
                lineupsQuantity.getLineupsQuantity(),
            );
            nhlNameStack = distributeFreeLineups({...nhlNameStack}, lineupsQuantity.getLineupsQuantity());
            if (nhlName.isDoubleStackMode) {
                nhlNameStack.teams.map((team) => {
                    let secondStack = createStackConstraintsObj(
                        Const.NHL_NAME_STACK_SECOND_GROUP,
                        team.reservedLineups,
                        team.name,
                    );
                    if (!isEmpty(secondStack.teams)) {
                        team.stack = distributeFreeLineups({...secondStack}, team.reservedLineups);
                    }
                });
            }
        }

        result += createConstraintsStr(nhlNameStack);

        this.store.set('nhlNameStack', nhlNameStack);
        // console.log("result\n",result);
        return result;
    }

    prepareSectionByCaptainPosition_ShowDown({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let result = '';
        Object.keys(playersGroupedById).map((pId) => {
            let prefix = '+' + getSimplexUnitName(pId);
            result += addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
        });
        if (result.length) {
            result += '=1\n';
        }
        // console.log("captainPosition\n",result);
        return result;
    }

    prepareSectionByCreatedLineups({incomingValues = {}}) {
        const {sportConfig} = Action.getState();
        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'createdLineups',
            {
                ...incomingValues,
                checkIfPlayerOnDoublePosition: true,
            },
            [],
        );

        let rules = '';
        if (!isEmpty(constraints)) {
            rules = this.exec(
                'convertConstraintsToStr',
                {
                    constraints,
                },
                '',
            );
        }
        // console.log("createdLineups\n", rules);
        return rules;
    }

    prepareSectionByCreatedLineups_ShowDown({incomingValues = {}}) {
        const {sportConfig, uniquePlayersQuantity} = Action.getState();

        let result = '';
        this.store.get('lineups', []).map((lineup) => {
            result += lineup;
            let residual =
                sportConfig.getConfigProp('playersInTeam') - uniquePlayersQuantity.getUniquePlayersQuantity();
            result += '<=' + residual + '\n';
        });

        return result;
    }

    prepareSectionByCreatedLineups_Tiers({incomingValues = {}}) {
        let state = Action.getState();
        const {uniquePlayersQuantity, sportConfig} = state;
        const {preparedPlayers, slates} = state.playersData;
        const playersGroupedByPlayerId = preparedPlayers.groupedBy.playerId();
        const slate = slates.getById(sportConfig.getSlateId());

        let result = '';
        this.store.get('usedPlayers', []).map((lineup) => {
            lineup.map((playerId) => {
                result +=
                    '+' +
                    preparePlayerUID(playersGroupedByPlayerId[playerId])
                        .map((playerId) => getSimplexUnitName(playerId))
                        .join('+');
            });

            let residual = slate.tiers_quantity - uniquePlayersQuantity.getUniquePlayersQuantity();
            result += '<=' + residual + '\n';
        });
        // console.log("createdLineups\n",result);
        return result;
    }

    prepareSectionByExposureMax({incomingValues = {}}) {
        const {
            exposureMode,
            lineupsQuantity,
            lineupsData: {filters: lineupsFilter},
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed()) {
            return '';
        }

        const players = preparedPlayers.groupedBy.property('exposureMax');

        let lineups = this.store.get('lineups', []),
            denyConstraints = this.store.get('denyConstraints', []),
            playersForbiddenByExposureMax = [],
            exposureMax = {};

        if (!isEmpty(players) && !inArray('exposureMax', denyConstraints)) {
            // if (lineupsFilter.exposureMode == Const.EXPOSURE_MODE_DISTRIBUTED) {
            //
            //     const createStr = function(playersArr, subtrackQ = 0)
            //     {
            //         let str = '';
            //
            //         if (playersArr.length < 1) {
            //             return str;
            //         }
            //
            //         let playerUIDs = [];
            //         playersArr.map(playerId => {
            //             $.merge(playerUIDs, preparePlayerUID(playersGroupedByPlayerId[playerId]));
            //         });
            //
            //         str += '+' + Prefix + playerUIDs.join('+' + Prefix);
            //
            //         /**
            //          * Subtrack 1 position in each lineup.
            //          * If players more than places in team,
            //          * then subtrack 1 position from quantity of places in team.
            //          * @type {number}
            //          */
            //         let maxQ = playerUIDs.length - subtrackQ;
            //         if (playerUIDs.length > sportConfig.playersInTeam) {
            //             maxQ = sportConfig.playersInTeam - subtrackQ;
            //         }
            //
            //         return str + '<=' + maxQ + "\n";
            //     }
            //
            //     /**
            //      * Build an array of players with max exposure less than 100%
            //      * @type {Array}
            //      */
            //     let availablePlayers = [];
            //     Object.keys(playersGroupedByExposureMax).map(exp => {
            //         playersGroupedByExposureMax[exp].map(player => {
            //             /**
            //              * Player who occupies enough lineups according to his max exposure,
            //              * has to be missed.
            //              * @type {any}
            //              */
            //             let usedInLineups = countPlayerIdInLineups(player.playerId, lineups.join(''));
            //             let usedInLineupsPercentage = (usedInLineups + 1) / lineupsFilter.lineupsCount * 100;
            //             if (usedInLineupsPercentage > player.exposureMax) {
            //                 exposureMinForbiddenPlayers.push(player.playerId);
            //                 h += Prefix + player.playerId + "=0\n";
            //             } else {
            //                 availablePlayers.push(player.playerId);
            //             }
            //         });
            //     });
            //
            //     /**
            //      * If there aren't at least 2 players,
            //      * return empty string.
            //      */
            //     if (availablePlayers.length < 1) {
            //         return h;
            //     }
            //
            //     let usedPlayers = [],
            //         notUsedPlayers = [];
            //
            //     if (! isEmpty(lineups)) {
            //         let lastLineup = lineups[lineups.length - 1];
            //         availablePlayers.map(playerId => {
            //             if (Player.countPlayerIdInLineups(playerId, lastLineup)) {
            //                 usedPlayers.push(playerId);
            //             } else {
            //                 notUsedPlayers.push(playerId);
            //             }
            //         });
            //         if (usedPlayers.length == 1) {
            //             exposureMinForbiddenPlayers.push(usedPlayers[0]);
            //         }
            //         h += createStr(usedPlayers, 1);
            //     } else {
            //         notUsedPlayers = $.extend([], availablePlayers);
            //     }
            //
            //     if (!isEmpty(usedPlayers) || notUsedPlayers.length <= 1) {
            //         h += createStr(notUsedPlayers, 0);
            //     } else {
            //         h += createStr(notUsedPlayers, 1);
            //     }
            //
            //     // h += createStr(arr, 1);
            //
            // } else
            // if (lineupsFilter.exposureMode == Const.EXPOSURE_MODE_BALANCED) {
            //
            //     Object.keys(playersGroupedByExposureMax).map(exp => {
            //         playersGroupedByExposureMax[exp].map(player => {
            //
            //             if (lineups.length == 0) {
            //                 exposureMinForbiddenPlayers.push(player.playerId);
            //                 h += '+' + Prefix + preparePlayerUID(player).join('+' + Prefix) + "=0\n";
            //             } else {
            //                 /**
            //                  * Player who occupies enough lineups according to his max exposure,
            //                  * has to be missed.
            //                  * @type {any}
            //                  */
            //                 let usedInLineups = 0;
            //                 let playerUIDs = preparePlayerUID(player);
            //                 playerUIDs.map(playerUID => {
            //                     usedInLineups += countPlayerIdInLineups(playerUID, lineups.join(''));
            //                 });
            //
            //                 let usedInLineupsPercentage = (usedInLineups + 1) / (lineups.length + 1) * 100;
            //                 if (usedInLineupsPercentage > player.exposureMax) {
            //                     exposureMinForbiddenPlayers.push(player.playerId);
            //                     h += '+' + Prefix + playerUIDs.join('+' + Prefix) + "=0\n";
            //                 }
            //             }
            //
            //         });
            //     });
            //
            //     // if (! isEmpty(availablePlayers)) {
            //     //     availablePlayers.map(playerId => {
            //     //         h += '+' + Prefix + preparePlayerUID(playersGroupedByPlayerId[playerId]).join('+' + Prefix);
            //     //     });
            //     //     h += "=0\n";
            //     // }
            //
            // } else
            if (exposureMode.isModeTournament()) {
                let calculatedLineups = this.store.get('calculatedLineupsCount', 0);
                Object.keys(players).map((exp) => {
                    if (0 >= calculatedLineups) {
                        return;
                    }

                    players[exp].map((player) => {
                        let playerUIDs = preparePlayerUID(player);

                        if (lineups.length == 0) {
                            playersForbiddenByExposureMax.push(player.playerId);
                            playerUIDs.map((pId) => (exposureMax[getSimplexUnitName(pId)] = 1));
                        } else {
                            // Player who occupies enough lineups according to his max exposure, has to be missed.
                            let usedInLineups = 0;
                            playerUIDs.map((playerUID) => {
                                usedInLineups += countPlayerIdInLineups(playerUID, lineups.join(''));
                            });

                            let usedInLineupsPercentage = ((usedInLineups + 1) / (lineups.length + 1)) * 100;
                            if (usedInLineupsPercentage > player.exposureMax) {
                                playersForbiddenByExposureMax.push(player.playerId);
                                playerUIDs.map((pId) => (exposureMax[getSimplexUnitName(pId)] = 1));
                            }
                        }
                    });
                });
            } else {
                Object.keys(players).map((exp) => {
                    players[exp].map((player) => {
                        let playerUIDs = preparePlayerUID(player);

                        let usedInLineups = 0;
                        playerUIDs.map((playerUID) => {
                            usedInLineups += countPlayerIdInLineups(playerUID, lineups.join(''));
                        });

                        let usedInLineupsPercentage =
                            ((usedInLineups + 1) / lineupsQuantity.getLineupsQuantity()) * 100;
                        if (usedInLineupsPercentage > player.exposureMax) {
                            playersForbiddenByExposureMax.push(player.playerId);
                            playerUIDs.map((pId) => (exposureMax[getSimplexUnitName(pId)] = 1));
                        }
                    });
                });
            }
        }

        this.store.set('playersForbiddenByExposureMax', playersForbiddenByExposureMax);

        let result = '';
        if (!isEmpty(exposureMax)) {
            result = '+' + Object.keys(exposureMax).join('+') + Const.STACKS_TYPE_AT_MOST + '0\n';
        }
        // console.log('expMax',{...result});
        return result;
    }

    prepareSectionByExposureMax_ShowDown({incomingValues = {}}) {
        const {
            exposureMode,
            lineupsQuantity,
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();

        const {modes = [Const.FLEX_MODE, Const.CAPTAIN_MODE]} = incomingValues;

        const lineups = this.store.get('lineups', []);
        const lineupsStr = lineups.join('');
        const playersForbiddenByExposureMax = [];
        const exposureMax = {};

        if (inArray('exposureMax', this.store.get('denyConstraints', []))) {
            return '';
        }

        const PlayerService = getPlayerService(sportConfig);

        modes.map((mode) => {
            const expMaxPropName = PlayerService.exec('definePropNameOfMode', {prop: 'exposureMax', mode});
            const playersGroupedByExposureMax = preparedPlayers.groupedBy.property(expMaxPropName);

            if (exposureMode.isModeTournament()) {
                let calculatedLineups = this.store.get('calculatedLineupsCount', 0);
                if (calculatedLineups) {
                    Object.values(playersGroupedByExposureMax).map((players) => {
                        players.map((player) => {
                            let lpPlayerId = addRoleSuffix({prefix: player.playerId, role: mode});

                            if (!lineups) {
                                playersForbiddenByExposureMax.push(lpPlayerId);
                                exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                            } else {
                                let usedInLineups = countPlayerIdInLineups(lpPlayerId, lineupsStr);
                                let usedInLineupsPercentage = calcPercentage(lineups.length + 1, usedInLineups + 1);

                                if (usedInLineupsPercentage > player.getPropValueOfMode('exposureMax', mode)) {
                                    playersForbiddenByExposureMax.push(lpPlayerId);
                                    exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                                }
                            }
                        });
                    });
                }
            } else {
                Object.values(playersGroupedByExposureMax).map((players) => {
                    players.map((player) => {
                        let lpPlayerId = addRoleSuffix({prefix: player.playerId, role: mode});
                        let usedInLineups = countPlayerIdInLineups(lpPlayerId, lineupsStr);
                        let usedInLineupsPercentage = calcPercentage(
                            lineupsQuantity.getLineupsQuantity(),
                            usedInLineups + 1,
                        );

                        if (usedInLineupsPercentage > player.getPropValueOfMode('exposureMax', mode)) {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        }
                    });
                });
            }
        });

        const playersGroupedByOverallExposureMax = preparedPlayers.groupedBy.property('overall_exposureMax');
        if (exposureMode.isModeTournament()) {
            let calculatedLineups = this.store.get('calculatedLineupsCount', 0);
            if (calculatedLineups) {
                Object.values(playersGroupedByOverallExposureMax).map((players) => {
                    players.map((player) => {
                        const lpPlayerIds = modes.map((mode) => addRoleSuffix({prefix: player.playerId, role: mode}));

                        if (!lineups) {
                            lpPlayerIds.map((lpPlayerId) => {
                                playersForbiddenByExposureMax.push(lpPlayerId);
                                exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                            });
                        } else {
                            let usedInLineups = 0;
                            lpPlayerIds.map((lpPlayerId) => {
                                usedInLineups += countPlayerIdInLineups(lpPlayerId, lineupsStr);
                            });

                            const usedInLineupsPercentage = calcPercentage(lineups.length + 1, usedInLineups + 1);

                            if (usedInLineupsPercentage > player.overall_exposureMax) {
                                lpPlayerIds.map((lpPlayerId) => {
                                    playersForbiddenByExposureMax.push(lpPlayerId);
                                    exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                                });
                            }
                        }
                    });
                });
            }
        } else {
            Object.values(playersGroupedByOverallExposureMax).map((players) => {
                players.map((player) => {
                    const lpPlayerIds = modes.map((mode) => addRoleSuffix({prefix: player.playerId, role: mode}));

                    let usedInLineups = 0;
                    lpPlayerIds.map((lpPlayerId) => {
                        usedInLineups += countPlayerIdInLineups(lpPlayerId, lineupsStr);
                    });

                    const usedInLineupsPercentage = calcPercentage(
                        lineupsQuantity.getLineupsQuantity(),
                        usedInLineups + 1,
                    );

                    if (usedInLineupsPercentage > player.overall_exposureMax) {
                        lpPlayerIds.map((lpPlayerId) => {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        });
                    }
                });
            });
        }

        this.store.set('playersForbiddenByExposureMax', arrayUnique(playersForbiddenByExposureMax));

        let result = '';
        if (!isEmpty(exposureMax)) {
            result = '+' + Object.keys(exposureMax).join('+') + Const.STACKS_TYPE_AT_MOST + '0\n';
        }
        // console.log("prepareSectionByExposureMax_ShowDown\n", result)
        return result;
    }

    prepareSectionByExposureMin({incomingValues = {}}) {
        const denyConstraints = this.store.get('denyConstraints', []);

        let result = '';
        if (inArray('exposureMin', denyConstraints)) {
            return result;
        }

        const {
            sportConfig,
            lineupsQuantity,
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed()) {
            return result;
        }

        const that = this;
        // const {exposureMinForbiddenPlayers: forbiddenPlayers} = incomingValues;
        const playersGroupedByExposureMin = preparedPlayers.groupedBy.property('exposureMin');
        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);

        /**
         * The main idea in order to solve the exposureMin problem contains two points.
         *
         * The first is:
         * to build 'exposure line' that will make the GLPK library use players from it.
         * Sample, if we need to use players x111 and x222 in lineups,
         * then the 'exposure line' will look like +x111+x222.
         * After that we need to define a coefficient.
         * Assume the player x111 has exposureMin value equal 0.4 and another one x222 has 0.7.
         * in this case coefficient will be next: 0.4 + 0.7 = 1.1
         * The next thing we need to do round up given coefficient - Math.ceil(1.1) = 2;
         * As a result full exposure line is: +x111+x222 >= 2;
         *
         * The second point is:
         * to set hard constraints for players which have to be in each residual lineups necessarily.
         * Sample, if player x111 have to be in each lineup,
         * expression will be: +x111 = 1;
         */
        if (isEmpty(playersGroupedByExposureMin)) {
            return result;
        }

        /**
         * Q means 'quantity';
         * residualLineupsQ - quantity lineups that should be calculated.
         */
        let residualLineupsQ = lineupsQuantity.getLineupsQuantity() - this.store.get('calculatedLineupsCount', 0),
            amountCoefficients = 0,
            hardConstraints = '',
            expLine = '',
            positionsCoefficient = {};
        Object.keys(playersGroupedByExposureMin).map((exp) => {
            playersGroupedByExposureMin[exp].map((player) => {
                let playerUIDs = preparePlayerUID(player),
                    usedInLineups = 0;
                playerUIDs.map((playerUID) => {
                    usedInLineups += countPlayerIdInLineups(playerUID, that.store.get('lineups', []).join(''));
                });

                /**
                 * reservedLineupsQ - quantity reserved lineups by one player.
                 * For instance if 'lineupsCount' was set in 100
                 * and some player has 'exposureMin' equal 20 then reservedLineupsQ will be 20.
                 */
                let reservedLineupsQ = calcQuantityOfReservedLineupsByExposure(
                    lineupsQuantity.getLineupsQuantity(),
                    player.exposureMin,
                );

                /**
                 * residualReservedLineupsQ - quantity of residual reserved lineups.
                 * For instance if common value of reserved lineups is 10
                 * and a player has already set in 2 lineups then 10 - 2 = 8
                 */
                let residualReservedLineupsQ = reservedLineupsQ - usedInLineups;

                /**
                 * If residualReservedLineupsQ is 0, move to the next iteration.
                 * It means there is no necessary to add him in the lineup.
                 */
                if (residualReservedLineupsQ > 0) {
                    /**
                     * If residualReservedLineupsQ equal residualLineupsQ,
                     * set hard constraints for this player.
                     * Need to specify that this player have to be in each next lineups.
                     */
                    if (residualReservedLineupsQ >= residualLineupsQ) {
                        if (!inArray(player.playerId, forbiddenPlayers)) {
                            hardConstraints +=
                                '+' +
                                preparePlayerUID(player)
                                    .map((playerId) => getSimplexUnitName(playerId))
                                    .join('+') +
                                '=1\n';
                        }
                    } else if (!inArray(player.playerId, forbiddenPlayers)) {
                        /**
                         * Calculate coefficient reserved places in one lineup.
                         * For instance if the first player has value 0.4,
                         * the second one 0.7 and the third one 0.1, then 0.4 + 0.7 + 0.1 = 1.3
                         */
                        let coefficientReservedPlaces = residualReservedLineupsQ / residualLineupsQ;

                        /** Shared coefficient by positions in order to verify that maxPlayers from config is not exceeded. */
                        player.position.map(function (pos) {
                            if (!positionsCoefficient.hasOwnProperty(pos)) {
                                positionsCoefficient[pos] = 0;
                            }
                            if (isPlayerOnDoublePosition(player)) {
                                coefficientReservedPlaces = coefficientReservedPlaces / 2;
                            }
                            positionsCoefficient[pos] += coefficientReservedPlaces;
                        });

                        /**
                         * Build line that makes the GLPK use these players during a creation of a lineup.
                         * For instance: +x111+x222+x333
                         */
                        expLine +=
                            '+' +
                            preparePlayerUID(player)
                                .map((playerId) => getSimplexUnitName(playerId))
                                .join('+');
                    }
                }
            });
        });

        /** If coefficient on one position is exceeded, set value from config. */
        const positions = sportConfig.getConfigProp('positions');
        Object.keys(positionsCoefficient).map((pos) => {
            let coef = positionsCoefficient[pos];
            if (positions.hasOwnProperty(pos)) {
                const position = sportConfig.getJointPosition(pos) || pos;
                const maxPlayers = Number(sportConfig.getMaxPlayersOnPosition(position));
                if (maxPlayers && coef > maxPlayers) {
                    coef = maxPlayers;
                }
            }
            amountCoefficients += coef;
        });

        /** If amountCoefficients is more than 0, set exposure line with a list of players which have to be in lineups. */
        if (amountCoefficients > 0 && expLine) {
            amountCoefficients = Math.ceil(amountCoefficients);
            if (amountCoefficients > sportConfig.getPlayersInTeam()) {
                amountCoefficients = sportConfig.getPlayersInTeam();
            }

            /** For instance: +x111+x222+x333 >= 2 */
            expLine += '>=' + amountCoefficients + '\n';
        }

        result = hardConstraints + expLine;
        // console.log("exposureMin\n",result);
        return result;
    }

    prepareSectionByExposureMin_ShowDown({incomingValues: {salaryMode}}) {
        const denyConstraints = this.store.get('denyConstraints', []);

        if (inArray('exposureMin', denyConstraints)) return '';

        const {
            sportConfig,
            lineupsQuantity,
            playersData: {preparedPlayers},
        } = Action.getState();

        const softConstraintsByMode = {};
        const playersQtyByMode = {};
        const simplexUnitNamesByPlayer = {};
        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        const residualLineupsQty = lineupsQuantity.getLineupsQuantity() - this.store.get('calculatedLineupsCount', 0);

        [Const.CAPTAIN_MODE, Const.DEFAULT_MODE].forEach((playerMode) => {
            const expMinPropName = getPlayerService(sportConfig).exec('definePropNameOfMode', {
                prop: 'exposureMin',
                mode: playerMode,
            });
            const playersGroupedByExposureMin = preparedPlayers.groupedBy.property(expMinPropName);

            Object.values(playersGroupedByExposureMin).forEach((players) => {
                players
                    .filter((player) => !player.isExcludedByMode(playerMode, {salaryMode: salaryMode || playerMode}))
                    .forEach((player) => {
                        const lpPlayerId = addRoleSuffix({prefix: player.getId(), role: playerMode});

                        if (inArray(lpPlayerId, forbiddenPlayers)) return;

                        const usedInLineups = countPlayerIdInLineups(lpPlayerId, this.store.get('lineups', []).join(''));
                        const reservedLineupsQty = calcQuantityOfReservedLineupsByExposure(
                            lineupsQuantity.getLineupsQuantity(),
                            player.getPropValueOfMode('exposureMin', playerMode),
                        );
                        const residualReservedLineupsQty = reservedLineupsQty - usedInLineups;

                        if (residualReservedLineupsQty <= 0) return;

                        const simplexUnitName = getSimplexUnitName(lpPlayerId);

                        simplexUnitNamesByPlayer[player.getId()] ??= {};
                        simplexUnitNamesByPlayer[player.getId()][simplexUnitName] = residualReservedLineupsQty;

                        if (residualReservedLineupsQty < residualLineupsQty) {
                            playersQtyByMode[playerMode] ??= 0;
                            playersQtyByMode[playerMode] += residualReservedLineupsQty / residualLineupsQty;
                            softConstraintsByMode[playerMode] ??= '';
                            softConstraintsByMode[playerMode] += '+' + simplexUnitName;
                        }
                    });
            });
        });

        let captainQty = 0;
        let totalQty = 0;
        const lineupPositionsQty = {
            [Const.CAPTAIN_MODE]: 1,
            [Const.DEFAULT_MODE]: sportConfig.getPlayersInTeam() - 1,
        };
        Object.keys(playersQtyByMode).forEach((playerMode) => {
            const modePlayersQty = (playersQtyByMode[playerMode] > lineupPositionsQty[playerMode])
                ? lineupPositionsQty[playerMode]
                : playersQtyByMode[playerMode];

            totalQty += modePlayersQty;
            (playerMode === Const.CAPTAIN_MODE) && (captainQty = Math.ceil(modePlayersQty));
        });

        const captainSoftConstraints = [];
        if (captainQty > 0 && softConstraintsByMode?.[Const.CAPTAIN_MODE].length) {
            captainSoftConstraints.push(softConstraintsByMode[Const.CAPTAIN_MODE] + '>=' + captainQty);
        }

        const totalSoftConstraints = [];
        if (totalQty > 0 && !isEmpty(softConstraintsByMode)) {
            totalQty = (totalQty > sportConfig.getPlayersInTeam())
                ? sportConfig.getPlayersInTeam()
                : Math.ceil(totalQty);

            totalSoftConstraints.push(Object.values(softConstraintsByMode).join('') + '>=' + totalQty);
        }

        const strictConstraints = [];
        Object.values(simplexUnitNamesByPlayer).map((simplexUnitNames) => {
            const playerLineupsQty = Object.values(simplexUnitNames).reduce((acc, lineupsQty) => acc + lineupsQty, 0);
            if (playerLineupsQty >= residualLineupsQty) {
                strictConstraints.push('+' + Object.keys(simplexUnitNames).join('+') + '=1');
            }
        });

        return [
            strictConstraints.join('\n'),
            captainSoftConstraints.join('\n'),
            totalSoftConstraints.join('\n'),
        ].join('\n');
    }

    prepareGeneralSection({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedByAllowedPositions = preparedPlayers.groupedBy.allowedPositions();

        let result = [],
            addedPlayers = [];

        playersGroupedByAllowedPositions.map((player) => {
            if (!inArray(player.uniqueId, addedPlayers)) {
                result.push(getSimplexUnitName(player.uniqueId));
                addedPlayers.push(player.uniqueId);
            }
        });
        //console.log('general',result);
        return result;
    }

    prepareGeneralSection_ShowDown({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let result = [];
        Object.keys(playersGroupedById).map((pId) => {
            let prefix = getSimplexUnitName(pId);
            result.push(addRoleSuffix({prefix, role: Const.CAPTAIN_MODE}));
            result.push(addRoleSuffix({prefix}));
        });
        //console.log("gen\n",result);
        return result;
    }

    prepareBoundSection({incomingValues = {}}) {
        const state = Action.getState();
        const {playersLock} = state;
        const {preparedPlayers} = state.playersData;
        const playersGroupedByPosition = preparedPlayers.groupedBy.position();
        const playersGroupedByPlayerId = preparedPlayers.groupedBy.playerId();
        const {projectionPointsType} = state;

        let result = {},
            addedPlayers = [];
        playersGroupedByPosition.map((player) => {
            if (!inArray(player.uniqueId, addedPlayers)) {
                if (player[projectionPointsType.getProjectionPointsType()] < 0) {
                    result[getSimplexUnitName(player.uniqueId)] = {
                        atLeast: 0,
                        atMost: 1,
                    };
                } else if (playersLock.isLockedAnywhere(player.getId())) {
                    if (!isPlayerOnDoublePosition(playersGroupedByPlayerId[player.playerId])) {
                        result[getSimplexUnitName(player.uniqueId)] = {
                            exactly: 1,
                        };
                    } else {
                        result[getSimplexUnitName(player.uniqueId)] = {
                            atLeast: 0,
                            atMost: 1,
                        };
                    }
                } else {
                    result[getSimplexUnitName(player.uniqueId)] = {
                        atLeast: 0,
                        atMost: 1,
                    };
                }
                addedPlayers.push(player.uniqueId);
            }
        });
        //console.log("bound\n",result);
        return result;
    }

    prepareBoundSection_ShowDown({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const playersGroupedById = preparedPlayers.groupedBy.playerId();

        let result = {};
        Object.keys(playersGroupedById).map((pId) => {
            let player = playersGroupedById[pId];
            let prefix = getSimplexUnitName(pId);

            const isExcludedByCapMode = player.isExcludedByMode(Const.CAPTAIN_MODE);
            result[addRoleSuffix({prefix, role: Const.CAPTAIN_MODE})] = {
                atLeast: 0,
                atMost: isExcludedByCapMode ? 0 : 1,
            };

            const isExcludedByFlexMode = player.isExcludedByMode(Const.FLEX_MODE);
            result[addRoleSuffix({prefix, role: Const.FLEX_MODE})] = {
                atLeast: 0,
                atMost: isExcludedByFlexMode ? 0 : 1,
            };
        });
        //console.log("bound\n",result);
        return result;
    }

    prepareMaxSection({incomingValues = {}}) {
        const {
            projectionPointsType,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersGroupedByPosition = preparedPlayers.groupedBy.position();
        const projPtsType = projectionPointsType.getProjectionPointsType();

        const row = {};
        const addedPlayers = [];
        playersGroupedByPosition.map((player) => {
            if (!inArray(player.uniqueId, addedPlayers)) {
                row[getSimplexUnitName(player.uniqueId)] = NumberHelper.float(player[projPtsType], 3);
                addedPlayers.push(player.uniqueId);
            }
        });

        return {row};
    }

    prepareMaxSection_ShowDown({incomingValues = {}}) {
        const {
            playersData: {preparedPlayers},
            projectionPointsType,
        } = Action.getState();
        const {modes = [Const.CAPTAIN_MODE, Const.DEFAULT_MODE]} = incomingValues;
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const projPtsType = projectionPointsType.getProjectionPointsType();

        const row = {};
        Object.keys(playersIndexedById).map((playerId) => {
            const player = playersIndexedById[playerId];
            const prefix = getSimplexUnitName(playerId);

            modes.map((mode) => {
                const unitName = addRoleSuffix({prefix, role: mode});
                row[unitName] = NumberHelper.float(player.getPropValueOfMode(projPtsType, mode), 3);
            });
        });

        return {row};
    }

    prepareMaxRandomSection({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const {randomPoints} = state;
        const players = preparedPlayers.groupedBy.position();
        const {rawLineups} = state.lineupsData;
        const {projectionPointsType} = state;

        let lineupNumber = 1 + rawLineups.getQuantity();

        let result = {},
            addedPlayers = [];

        if (randomPoints.items.hasOwnProperty(lineupNumber)) {
            const pointsList = randomPoints.items[lineupNumber];
            players.map((player) => {
                if (inArray(player.uniqueId, addedPlayers)) {
                    return;
                }

                let points;
                if (pointsList.hasOwnProperty(player.playerId)) {
                    points = pointsList[player.playerId] * 1;
                } else {
                    points = toFloat(player[projectionPointsType.getProjectionPointsType()], 3);
                }

                result[getSimplexUnitName(player.uniqueId)] = points;

                addedPlayers.push(player.uniqueId);
            });
        }
        // console.log("maxRandom\n",result);
        return {...result};
    }

    prepareMaxRandomSection_ShowDown({incomingValues = {}}) {
        const state = Action.getState();
        const {preparedPlayers} = state.playersData;
        const {randomPoints} = state;
        const players = preparedPlayers.groupedBy.playerId();
        const {rawLineups} = state.lineupsData;

        const lineupNumber = 1 + rawLineups.getQuantity();

        const result = {};

        if (randomPoints.items.hasOwnProperty(lineupNumber)) {
            const pointsList = randomPoints.items[lineupNumber];
            Object.keys(players).map((pId) => {
                {
                    let key = addRoleSuffix({prefix: pId, role: Const.CAPTAIN_MODE});
                    if (pointsList.hasOwnProperty(key)) {
                        result[getSimplexUnitName(key)] = pointsList[key] * 1;
                    }
                }
                {
                    let key = addRoleSuffix({prefix: pId});
                    if (pointsList.hasOwnProperty(key)) {
                        result[getSimplexUnitName(key)] = pointsList[key] * 1;
                    }
                }
            });
        }
        // console.log("maxRandom\n",result);
        return {...result};
    }

    defineFlexPositionsInLineupByPositionsOrder({incomingValues: {lineup}}) {
        let playerIds = [...lineup];
        const {sportConfig, playersData} = Action.getState();
        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const positionsOrder = sportConfig.getConfigProp('positionsOrder', {});

        Object.values(positionsOrder).map((position) => {
            for (let key in playerIds) {
                const playerId = playerIds[key];
                const player = players[playerId];
                if (position.alias === player.positionName) {
                    playerIds.splice(key, 1);
                    break;
                }
            }
        });

        const result = [];
        playerIds.map((playerId) => {
            const player = players[playerId];
            result.push(player.positionName);
        });
        // console.log('defineFlexPositionsInLineupByPositionsOrder',[...result]);
        return result;
    }

    getForbiddenFlexMaxExposurePositions({incomingValues: {flexPositionsQuantity = 1}}) {
        const {lineupsQuantity, playersData} = Action.getState();
        const {flexMaxExposurePositions} = playersData;

        const result = {};
        if (isEmpty(flexMaxExposurePositions)) {
            return result;
        }

        const flexPositionStats = this.store.get('flexPositionStats', {});
        const usedPlayers = this.store.get('usedPlayers', []);

        if (!isEmpty(usedPlayers)) {
            let lastLineup = usedPlayers[usedPlayers.length - 1];
            let flexPositions = this.exec('defineFlexPositionsInLineupByPositionsOrder', {
                lineup: lastLineup,
            });

            flexPositions.map((flexPosition) => {
                if (!flexPositionStats.hasOwnProperty(flexPosition)) {
                    flexPositionStats[flexPosition] = 0;
                }
                flexPositionStats[flexPosition]++;
            });

            this.store.set('flexPositionStats', flexPositionStats);
        }

        Object.keys(flexMaxExposurePositions).map((posName) => {
            let usageQuantity = flexMaxExposurePositions[posName];
            if (Const.DEFAULT_VALUE_FOR_MAX_EXPOSURE > usageQuantity) {
                let usedInLineups = 0;
                if (flexPositionStats.hasOwnProperty(posName)) {
                    usedInLineups = flexPositionStats[posName];
                }

                let usedInLineupsPercentage = (1 / lineupsQuantity.getLineupsQuantity()) * 100;
                if (usedInLineups) {
                    usedInLineupsPercentage = (usedInLineups / lineupsQuantity.getLineupsQuantity()) * 100;
                }

                if (usedInLineupsPercentage >= usageQuantity) {
                    result[posName] = flexPositionsQuantity;
                }
            }
        });
        // console.log('getForbiddenFlexMaxExposurePositions',{...result});
        return {...result};
    }

    opposingTeamsInLineup({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'opposingTeamsInLineup',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    opposingOpponentsInLineup({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'opposingOpponentsInLineup',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    nonControlledHittersStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'nonControlledHittersStacking',
            incomingValues,
            [],
        );

        return this.exec(
            'convertConstraintsToStr',
            {
                constraints,
            },
            '',
        );
    }

    nhlTeamStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('nhlTeamStacking', {}, []);

        return this.exec(
            'convertConstraintsToStr',
            {
                constraints,
            },
            '',
        );
    }

    nhlLegacyTeamStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store})
            .exec('nhlLegacyTeamStacking', {}, []);

        return this.exec('convertConstraintsToStr', {
            constraints,
        }, '');
    }

    nhlNonControlledStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'nhlNonControlledStacking',
            {},
            [],
        );

        return this.exec(
            'convertConstraintsToStr',
            {
                constraints,
            },
            '',
        );
    }

    singlegameForcedStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'singlegameForcedStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    stackRandomTeam({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'stackRandomTeam',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    correlationStackingPrimaryRule({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'correlationStackingPrimaryRule',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    correlationStackingSecondaryRule({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'correlationStackingSecondaryRule',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    correlationStackingUniversalRule({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'correlationStackingUniversalRule',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    correlationStackingRestrictedPlayers({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'correlationStackingRestrictedPlayers',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    additionalObjective({incomingValues = {}}) {
        let result = {};

        /**
         * Extra objective items which can be added during forming of constraints.
         * E.g. non controlled hitters stack.
         */
        const additionalObjective = this.store.get('additionalObjective', {});

        if (!isEmpty(additionalObjective)) {
            Object.values(additionalObjective).map((item) => {
                result = {...result, ...item};
            });
        }

        return result;
    }

    additionalBound({incomingValues = {}}) {
        const additionalObjective = this.exec('additionalObjective', {});

        const result = {};

        if (!isEmpty(additionalObjective)) {
            Object.keys(additionalObjective).map((item) => {
                result[item] = {atLeast: 0, atMost: 1};
            });
        }

        return result;
    }

    additionalGeneral({incomingValues = {}}) {
        const additionalObjective = this.exec('additionalObjective', {});

        return !isEmpty(additionalObjective) ? Object.keys(additionalObjective) : [];
    }

    convertConstraintsToStr({incomingValues = {}}) {
        let {constraints} = incomingValues;

        let result = '';

        if (isEmpty(constraints)) {
            return result;
        }

        if (!Array.isArray(constraints)) {
            constraints = [constraints];
        }

        Object.values(constraints).map((item) => {
            Object.keys(item.row).map((key) => {
                if (0 <= item.row[key]) {
                    result += '+';
                }
                result += item.row[key] + key;
            });

            if (item.hasOwnProperty('type') && item.hasOwnProperty('obj')) {
                result += getComparisonTypeByNumber(item.type);
                result += item.obj;
            }

            result += '\n';
        });

        return result;
    }

    convertConstraintsOfMultiPositionPlayersToStr({incomingValues = {}}) {
        let {constraints} = incomingValues;

        let result = '';

        if (isEmpty(constraints)) {
            return result;
        }

        if (!Array.isArray(constraints)) {
            constraints = [constraints];
        }

        const {
            playersData: {players},
        } = Action.getState();

        Object.values(constraints).map((item) => {
            Object.keys(item.row).map((simplexItem) => {
                const value = item.row[simplexItem];

                if (isUnitPrefixInSimplexItem(simplexItem)) {
                    const pId = extractEntityIdFromSimplexItem(simplexItem);
                    const playerUIDs = preparePlayerUID(players.getPlayer(pId));
                    playerUIDs.map((pId) => {
                        result += createConstraintItem(getSimplexUnitName(pId), value);
                    });
                } else {
                    result += createConstraintItem(simplexItem, value);
                }
            });

            if (item.hasOwnProperty('type') && item.hasOwnProperty('obj')) {
                result += getComparisonTypeByNumber(item.type);
                result += item.obj;
            }

            result += '\n';
        });

        function createConstraintItem(simplexItem, value) {
            let constraintItem = '';
            if (0 <= value) {
                constraintItem += '+'; // for cases when value is negative
            }
            constraintItem += value + simplexItem;
            return constraintItem;
        }

        return result;
    }

    convertGeneralToStr({incomingValues = {}}) {
        const {arr} = incomingValues;

        let result = '';
        if (!isEmpty(arr)) {
            result += arr.join('\n');
        }

        return result;
    }

    convertBoundToStr({incomingValues = {}}) {
        const {constraints} = incomingValues;

        let result = '';
        if (!isEmpty(constraints)) {
            Object.keys(constraints).map((key) => {
                const constraint = constraints[key];

                if (constraint.hasOwnProperty('exactly')) {
                    result += key + '=' + constraint.exactly + '\n';
                    return;
                }

                let row = '';
                if (constraint.hasOwnProperty('atMost')) {
                    row = key + '<=' + constraint.atMost;
                    if (constraint.hasOwnProperty('atLeast')) {
                        row = constraint.atLeast + '<=' + row;
                    }
                }

                result += row + '\n';
            });
        }

        return result;
    }

    teamExposureMax({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'teamExposureMax',
            {...incomingValues},
            [],
        );

        return this.exec(
            'convertConstraintsOfMultiPositionPlayersToStr',
            {
                constraints,
            },
            '',
        );
    }

    playersWhoseGamesAreLocked({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'playersWhoseGamesAreLocked',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    positions({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('positions', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    passReceiver({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'passReceiver',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    flexMaxExposure({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'flexMaxExposure',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    player({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('player', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    salary({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('salary', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    multiPositions({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'multiPositions',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    exposureMax({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('exposureMax', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    exposureMin({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('exposureMin', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    createdLineups({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'createdLineups',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    team({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('team', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    captainPositions({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'captainPositions',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    uniquePositions({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'uniquePositions',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    objectiveMax({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        return getSimplexService(sportConfig, {store: this.store}).exec('objectiveMax', incomingValues, {});
    }

    minTeams({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('minTeams', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    limitPositions({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'limitPositions',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    player({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec('player', incomingValues, []);

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    nhlNameStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'nhlNameStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    nhlNonControlledStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'nhlNonControlledStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    flexExposureMax({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'flexExposureMax',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    perTeamDriverLimits({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'perTeamDriverLimits',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    captainToConstructorStacking({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'captainToConstructorStacking',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    stackQuarterbackWithPassReceivers({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'stackQuarterbackWithPassReceivers',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    projectionBoostRule({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'projectionBoostRule',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    limitOnePitcherPerGame({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'limitOnePitcherPerGame',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    maxPlayersFromTeam({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'maxPlayersFromTeam',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    minTeamsInLineup({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'minTeamsInLineup',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    flexMaxExposure({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'flexMaxExposure',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }

    limitPositionsPerLineup({incomingValues = {}}) {
        const {sportConfig} = Action.getState();

        const constraints = getSimplexService(sportConfig, {store: this.store}).exec(
            'limitPositionsPerLineup',
            incomingValues,
            [],
        );

        return this.exec('convertConstraintsToStr', {constraints}, '');
    }
}
